import type {
    AdditionalProductFragment,
    StoryPageFragment,
    VariantFragment,
} from '@graphql/generated/graphql';
import type { TShoppable, TShoppableVariant } from '@hello-pimster/pimster-ui';
import { CompanyConfig } from '@lib/config/getCompanyConfig';
import { Platform } from '@lib/embed/providers/EmbedProvider';
import { transform } from '@lib/string-rewriter';
import { isSomething } from '@utils/isSomething';
import { getFormattedPrice } from '@utils/price';

import { toImage } from './image';

export type BuildContext = {
    companyConfig?: CompanyConfig;
    platform?: Platform;
};

export const getStoryPageAdditionalProducts = (
    storyPage?: StoryPageFragment | null
): (AdditionalProductFragment & {
    selectedVariant?: VariantFragment | null;
})[] => {
    const components = storyPage?.CallToAction?.map((component) =>
        component?.__typename === 'ComponentContentAdditionalProduct'
            ? component
            : undefined
    );
    return (
        components
            ?.map((c) =>
                c?.Additional_product
                    ? {
                          ...c.Additional_product,
                          selectedVariant: c.Selected_variant,
                      }
                    : undefined
            )
            .filter(isSomething) || []
    );
};

export const resolveShoppableUrl = (
    shoppable: AdditionalProductFragment | VariantFragment,
    context: BuildContext
): string | undefined => {
    const originalUrl = shoppable.Url;
    if (!originalUrl || !context.platform || !context.companyConfig) {
        return originalUrl ?? undefined;
    }
    const { platform, companyConfig } = context;
    if (
        platform === 'web' ||
        !companyConfig.additionalProduct.deeplinkRewriteRules
    ) {
        return originalUrl;
    }
    const rule = companyConfig.additionalProduct.deeplinkRewriteRules[platform];
    if (rule) return transform(originalUrl, rule) || originalUrl;
    return originalUrl;
};

export const toVariant = (
    v: VariantFragment,
    context: BuildContext
): TShoppableVariant => ({
    id: v.id,
    source: v.Source,
    name: v.Name,
    url: resolveShoppableUrl(v, context) || '',
    image: toImage(v.Images?.[0], v.ImageUrl, v.Name),
    price: v.Price ? getFormattedPrice(v.Price, v.locale || 'en') : undefined,
    options: v.Options || [],
    availability:
        (v.Availability as TShoppableVariant['availability']) || 'deleted',
    gtin: v.Gtin || undefined,
});

export const toShoppable = (
    s: AdditionalProductFragment & { selectedVariant?: VariantFragment | null },
    context: BuildContext
): TShoppable => ({
    id: s.id,
    source: s.Source,
    name: s.Name,
    url: resolveShoppableUrl(s, context) || '',
    image: toImage(s.Image, s.ImageUrl, s.Name),
    price: s.PriceV2
        ? getFormattedPrice(s.PriceV2, s.locale || 'en')
        : undefined,
    description: s.Description || undefined,
    variants: s.Variants?.filter(isSomething).map((v) => toVariant(v, context)),
    ...(s.selectedVariant && {
        selectedVariant: toVariant(s.selectedVariant, context),
    }),
});
